import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Upload } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useProjectSiteNavigation } from '@/shared/lib';
import { ConsignmentSelectionBar } from '@/features/consignment-selection';
import { SrfUploadModal } from '@/features/srf-upload';
import { ConsignmentsDataTable } from '@/widgets/consignments-data-table';

import { useConsignments } from '../lib';
import { getColumns, getColumnPresets } from '../model';
import { RouteNames } from '@/shared/config/routes';
import { MANUAL_NODES } from '@/shared/config/manual';

export function ConsignmentsPage() {
  const { t } = useTranslation();
  const { navigateWithContext } = useProjectSiteNavigation();
  const [isSrfModalOpen, setIsSrfModalOpen] = useState(false);
  const {
    tableData,
    isLoading,
    isFetchingNextPage,
    error,
    isValid,
    hasMore,
    handleLoadMore,
    selectedIds,
    setSelectedIds,
    sortField,
    sortDirection,
    handleSort,
    handleExport,
    isExporting,
    canExport,
    allSites
  } = useConsignments();

  const columns = useMemo(() => getColumns(t), [t]);
  const columnPresets = useMemo(() => getColumnPresets(t), [t]);

  // The title carries the viewpoint set by the header toggle
  const title = useMemo(() => {
    const viewpoint = allSites
      ? t('location.project.label', 'Project')
      : t('location.parentSite.label', 'Location Group');

    return `${t('cmrlist.title')}: ${viewpoint}`;
  }, [allSites, t]);

  if (!isValid) {
    return (
      <Alert>
        <AlertDescription>{t('Invalid project or site parameters. Please check the URL.')}</AlertDescription>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{t('Failed to load consignments. Please try again later.')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button size="sm" variant="outline" className="w-full sm:w-auto" onClick={() => setIsSrfModalOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            {t('srf.upload.button', 'Upload SRF')}
          </Button>
          <Button
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => navigateWithContext(RouteNames.CONSIGNMENT_NEW)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('cmrlist.create.title', 'Create New Consignment')}
          </Button>
        </div>

        <ConsignmentsDataTable
          data={tableData}
          columns={columns}
          title={title}
          helpTarget={MANUAL_NODES.CONSIGNMENTS_LIST}
          tableId="consignments-table"
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onClearSelection={() => setSelectedIds([])}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          enableFullscreen
          enableColumnCustomization
          enableExport={canExport}
          onExport={handleExport}
          isExporting={isExporting}
          columnPresets={columnPresets}
        />
      </div>

      <ConsignmentSelectionBar
        selectedIds={selectedIds}
        consignments={tableData}
        onClearSelection={() => setSelectedIds([])}
      />

      <SrfUploadModal isOpen={isSrfModalOpen} onClose={() => setIsSrfModalOpen(false)} />
    </>
  );
}
